import React, { ForwardRefRenderFunction, forwardRef } from "react";
//#region types
import { UtilFormTypes } from "models/formTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { CostForwardingSource } from "models/utilTypes/costForwardingTypes";
import { TranslationKeys } from "translations";
import { isCostForwardingRemoval, saveMarkingFromDialog } from "./costForwardingMarkingModel";
//#endregion
//#region components
import { useFloatingMessages } from "components/common";
import { useWintForm, WintForm } from "components/common/wintForms";
import { Grid } from "@mui/material";
import { useCostForwardingService } from "serviceHooks";
import CostForwardingCustomerFields from "./costForwardingCustomerFields";
import { costForwardingMarkDialogValidationFields, costForwardingValidationFields } from "./costForwardingValidation";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import * as Yup from "yup";

interface CostForwardingDialogFormProps {
  source: CostForwardingSource;
  sourceId: number;
  defaultValues: UtilFormTypes.CostForwardingMarking;
  /** Set by the dialog layout, which clones its single child with the ref and this callback. */
  onFormValidationChange?: (isValid: boolean) => void;
}

const markSchema = Yup.object<UtilFormTypes.CostForwardingMarking>(costForwardingMarkDialogValidationFields);
const editSchema = Yup.object<UtilFormTypes.CostForwardingMarking>(costForwardingValidationFields);

/** The document page's dialog body; the dialog's save action submits it through the ref. */
const CostForwardingDialogForm: ForwardRefRenderFunction<FormHandles, CostForwardingDialogFormProps> = (
  { source, sourceId, defaultValues, onFormValidationChange },
  ref
) => {
  const { putMarking } = useCostForwardingService();
  const { enqueueSuccessMessage } = useFloatingMessages();

  const handleSubmit = (values: UtilFormTypes.CostForwardingMarking) =>
    saveMarkingFromDialog(
      values,
      (marking) => putMarking(source, sourceId, marking),
      (marking) =>
        enqueueSuccessMessage(
          LocalizationHelperInstance.getFormattedMessage(
            isCostForwardingRemoval(marking)
              ? TranslationKeys.CostForwarding.Removed
              : TranslationKeys.CostForwarding.Saved
          )
        )
    );

  const { wintFormProps, connect } = useWintForm<UtilFormTypes.CostForwardingMarking>({
    formRef: ref,
    defaultValues,
    validationSchema: defaultValues.costForwardingState === undefined ? markSchema : editSchema,
    handleSubmit,
    onFormValidationChange,
  });

  return (
    <WintForm {...wintFormProps}>
      <Grid container direction="column">
        <CostForwardingCustomerFields connect={connect} presentation="dialog" />
      </Grid>
    </WintForm>
  );
};

export default forwardRef(CostForwardingDialogForm);
